import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ManageAddressesPage } from './ManageAddressesPage';
import MyOrders from './MyOrders';
import { FaCalendarCheck, FaClock, FaStethoscope, FaFileMedical, FaDownload, FaBan, FaTimes, FaSave, FaRedo, FaEdit, FaEye } from 'react-icons/fa';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
  profilePictureUrl?: string;
}

interface ProfilePageProps {
  onLogout: () => void;
  onProfileUpdate: (user: UserProfile) => void;
}
// ==========================================
// 1. UPDATED APPOINTMENT HISTORY COMPONENT
// Sirf is block ko ProfilePage.tsx me replace karna hai.
// Profile / Address / Orders ka code same rehne do.
// ==========================================
interface Appointment {
  id: string;
  booking_id: string | null;

  appointmentDate: string;
  appointmentTime: string;
  rawDate?: string;
  rawTime?: string;

  department: string;
  status: string;
  fullName: string;
  symptoms: string;
  report_url: string | null;
  payment_proof_url?: string | null;

  age?: string;
  dob?: string;
  gender?: string;
  email?: string;
  address?: string;
  phone?: string;

  doctor_name?: string | null;
  doctor_email?: string | null;
  video_link?: string | null;

  join_status?: string | null;
  patient_join_status?: string | null;
  doctor_join_status?: string | null;
  patient_joined_at?: string | null;
  doctor_joined_at?: string | null;

  link_active_from?: string | null;
  link_active_until?: string | null;
  canJoinNow?: number;
  isBeforeJoinWindow?: number;
  isLinkExpired?: number;

  consultation_notes?: string | null;
  prescription_text?: string | null;
  prescription_pdf_url?: string | null;
  completed_at?: string | null;

  missed_reason?: string | null;
  no_show_type?: string | null;
}

interface TimeSlot {
  time: string;
  isBooked: boolean;
  isPast?: boolean;
}

const AppointmentHistory: React.FC<{ mobile: string }> = ({ mobile }) => {
  const backendUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:8483"
      : "https://www.ayushvatika.com";

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [updating, setUpdating] = useState(false);

  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editSymptoms, setEditSymptoms] = useState("");
  const [editDepartment, setEditDepartment] = useState("");

  const [cancelReason, setCancelReason] = useState("Patient Request");
  const [cancelMessage, setCancelMessage] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];

  const normalizeStatus = (status?: string) => {
    return (status || "").toUpperCase().trim();
  };

  const finalStatuses = [
    "COMPLETED",
    "PATIENT_MISSED",
    "DOCTOR_NO_SHOW",
    "CLINIC_MISSED",
    "MISSED",
    "CANCELLED",
    "REJECTED",
  ];

  const activeStatuses = [
    "SCHEDULED",
    "DOCTOR_ASSIGNED",
    "LINK_READY",
    "WAITING",
    "IN_CONSULTATION",
    "RESCHEDULED",
  ];

  const toYMD = (dateValue: string) => {
    if (!dateValue) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      const [d, m, y] = dateValue.split("/");
      return `${y}-${m}-${d}`;
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  const getAppointmentDateTime = (appt: Appointment) => {
    const dateYMD = appt.rawDate || toYMD(appt.appointmentDate);
    const time24 = appt.rawTime || "";

    if (!dateYMD || !time24) return null;

    const dt = new Date(`${dateYMD}T${time24}:00`);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const formatTime12Hour = (time24: string) => {
    if (!time24) return "";
    const [hour, min] = time24.split(":");
    const h = parseInt(hour, 10);
    if (Number.isNaN(h)) return time24;
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${min} ${ampm}`;
  };

  const isFinalStatus = (appt: Appointment) => {
    return finalStatuses.includes(normalizeStatus(appt.status));
  };

  const isFutureAppointment = (appt: Appointment) => {
    const status = normalizeStatus(appt.status);
    const apptDateTime = getAppointmentDateTime(appt);

    if (!apptDateTime) return false;

    return (
      apptDateTime > new Date() &&
      ["SCHEDULED", "DOCTOR_ASSIGNED", "LINK_READY", "PENDING_APPROVAL", "RESCHEDULED"].includes(status)
    );
  };

  const canUpdateOrCancel = (appt: Appointment) => {
    const status = normalizeStatus(appt.status);
    return isFutureAppointment(appt) && !finalStatuses.includes(status) && status !== "IN_CONSULTATION";
  };

  const canReAppointment = (appt: Appointment) => {
    const status = normalizeStatus(appt.status);
    const apptDateTime = getAppointmentDateTime(appt);

    if (!apptDateTime) return false;
    if (!["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(status)) {
      return false;
    }

    const oneMonthLater = new Date(apptDateTime);
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

    return new Date() <= oneMonthLater;
  };

  const getMaxReAppointmentDate = (appt: Appointment) => {
    const apptDateTime = getAppointmentDateTime(appt);
    if (!apptDateTime) return undefined;

    const oneMonthLater = new Date(apptDateTime);
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

    return oneMonthLater.toISOString().split("T")[0];
  };

  const canJoinConsultation = (appt: Appointment) => {
    const status = normalizeStatus(appt.status);

    return (
      !!appt.video_link &&
      Number(appt.canJoinNow) === 1 &&
      !isFinalStatus(appt) &&
      status !== "PENDING_APPROVAL" &&
      appt.patient_join_status !== "JOINED"
    );
  };

  const getJoinMessage = (appt: Appointment) => {
    const status = normalizeStatus(appt.status);

    if (status === "PENDING_APPROVAL") {
      return "Payment verify hone ke baad doctor/link details dikhengi.";
    }

    if (!appt.doctor_name && !appt.video_link) {
      return "Doctor/link clinic side se assign kiya jayega.";
    }

    if (appt.doctor_name && !appt.video_link) {
      return `Doctor ${appt.doctor_name} assigned hai. Video link clinic side se ready hoga.`;
    }

    if (Number(appt.isBeforeJoinWindow) === 1) {
      return `Join button ${appt.link_active_from || "appointment time se 5 minute pehle"} active hoga.`;
    }

    if (appt.patient_join_status === "JOINED") {
      return "Aap video consultation me joined mark ho chuke hain.";
    }

    if (Number(appt.canJoinNow) === 1) {
      return "Join button active hai. Ab aap video consultation join kar sakte hain.";
    }

    if (Number(appt.isLinkExpired) === 1) {
      return "Join window expire ho chuka hai.";
    }

    return "Join abhi available nahi hai.";
  };

  const getFileUrl = (url?: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${backendUrl}${url}`;
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/appointments/history/${mobile}`);

      if (response.data.success) {
        setAppointments(response.data.data || []);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error("Failed to fetch appointment history", error);
      toast.error("Could not load appointment history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mobile) return;

    fetchHistory();

    const timer = window.setInterval(() => {
      fetchHistory();
    }, 60000);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile]);

  const fetchSlotsForDate = async (date: string) => {
    try {
      const response = await axios.get(`${backendUrl}/api/appointments/slots`, {
        params: { date },
      });
      setTimeSlots(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Could not load time slots.");
    }
  };

  const handleJoinConsultation = async (appt: Appointment) => {
    try {
      const response = await axios.put(`${backendUrl}/api/appointments/${appt.id}/patient-join`);

      toast.success(response.data?.message || "Joined successfully.");

      const finalVideoLink = response.data?.videoLink || appt.video_link;
      if (finalVideoLink) {
        window.open(finalVideoLink, "_blank", "noopener,noreferrer");
      }

      await fetchHistory();

      if (selectedAppt?.id === appt.id) {
        setSelectedAppt((prev) =>
          prev
            ? {
                ...prev,
                patient_join_status: "JOINED",
                status: "WAITING",
              }
            : prev
        );
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Join nahi ho paaya.");
    }
  };

  const getStatusBadge = (status: string) => {
    const finalStatus = normalizeStatus(status);

    switch (finalStatus) {
      case "PENDING_APPROVAL":
        return (
          <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange-700">
            Pending Approval
          </span>
        );

      case "SCHEDULED":
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            Scheduled
          </span>
        );

      case "DOCTOR_ASSIGNED":
        return (
          <span className="inline-flex items-center rounded-full bg-cyan-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-cyan-700">
            Doctor Assigned
          </span>
        );

      case "LINK_READY":
        return (
          <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-indigo-700">
            Link Ready
          </span>
        );

      case "WAITING":
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-yellow-700">
            Waiting
          </span>
        );

      case "IN_CONSULTATION":
        return (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-purple-700">
            In Consultation
          </span>
        );

      case "COMPLETED":
        return (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-700">
            Completed
          </span>
        );

      case "PATIENT_MISSED":
        return (
          <span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-rose-700">
            Patient Missed
          </span>
        );

      case "DOCTOR_NO_SHOW":
        return (
          <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange-700">
            Doctor No Show
          </span>
        );

      case "CLINIC_MISSED":
        return (
          <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-700">
            Clinic Missed
          </span>
        );

      case "CANCELLED":
        return (
          <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-700">
            Cancelled
          </span>
        );

      case "MISSED":
        return (
          <span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-rose-700">
            Missed
          </span>
        );

      case "RESCHEDULED":
        return (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-purple-700">
            Rescheduled
          </span>
        );

      default:
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
            {status || "Pending"}
          </span>
        );
    }
  };

  const getStatusDot = (status: string) => {
    const finalStatus = normalizeStatus(status);

    if (["SCHEDULED", "DOCTOR_ASSIGNED", "LINK_READY", "WAITING", "IN_CONSULTATION", "COMPLETED"].includes(finalStatus)) {
      return "bg-emerald-500";
    }

    if (finalStatus === "PENDING_APPROVAL") return "bg-orange-500";

    if (["CANCELLED", "MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(finalStatus)) {
      return "bg-red-500";
    }

    if (finalStatus === "RESCHEDULED") return "bg-purple-500";

    return "bg-blue-500";
  };

  const getStatusText = (status: string) => {
    const finalStatus = normalizeStatus(status);

    if (finalStatus === "PENDING_APPROVAL") return "Payment verification pending";
    if (finalStatus === "SCHEDULED") return "Appointment confirmed";
    if (finalStatus === "DOCTOR_ASSIGNED") return "Doctor assigned";
    if (finalStatus === "LINK_READY") return "Video link ready";
    if (finalStatus === "WAITING") return "Waiting for consultation";
    if (finalStatus === "IN_CONSULTATION") return "Consultation running";
    if (finalStatus === "COMPLETED") return "Consultation completed";
    if (finalStatus === "PATIENT_MISSED") return "Patient did not join in time";
    if (finalStatus === "DOCTOR_NO_SHOW") return "Doctor did not join in time";
    if (finalStatus === "CLINIC_MISSED") return "Clinic did not prepare link in time";
    if (finalStatus === "CANCELLED") return "Appointment cancelled";
    if (finalStatus === "MISSED") return "Appointment missed";
    if (finalStatus === "RESCHEDULED") return "Date/time updated";

    return "Status updated";
  };

  const openDetails = (appt: Appointment) => {
    setSelectedAppt(appt);
    setShowDetailsModal(true);
  };

  const openUpdate = async (appt: Appointment, mode: "UPDATE" | "REAPPOINT") => {
    setSelectedAppt(appt);

    setEditDate(mode === "REAPPOINT" ? "" : appt.rawDate || toYMD(appt.appointmentDate));
    setEditTime(mode === "REAPPOINT" ? "" : appt.rawTime || "");
    setEditSymptoms(appt.symptoms || "");
    setEditDepartment(appt.department || "");
    setTimeSlots([]);

    if (mode === "UPDATE") {
      const dateYMD = appt.rawDate || toYMD(appt.appointmentDate);
      if (dateYMD) await fetchSlotsForDate(dateYMD);
    }

    setShowUpdateModal(true);
  };

  const openCancel = (appt: Appointment) => {
    setSelectedAppt(appt);
    setCancelReason("Patient Request");
    setCancelMessage("");
    setShowCancelModal(true);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedAppt) return;

    if (!editDate || !editTime) {
      toast.error("Please select appointment date and time.");
      return;
    }

    try {
      setUpdating(true);

      const formData = new FormData();
      formData.append("appointmentId", selectedAppt.id);
      formData.append("mobile", mobile);
      formData.append("email", selectedAppt.email || "");
      formData.append("fullName", selectedAppt.fullName || "");
      formData.append("dob", selectedAppt.dob ? toYMD(selectedAppt.dob) : "");
      formData.append("age", selectedAppt.age || "");
      formData.append("gender", selectedAppt.gender || "");
      formData.append("address", selectedAppt.address || "");
      formData.append("department", editDepartment || selectedAppt.department || "");
      formData.append("symptoms", editSymptoms || selectedAppt.symptoms || "");
      formData.append("appointmentDate", editDate);
      formData.append("appointmentTime", editTime);
      formData.append("removeReport", "false");

      if (["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(normalizeStatus(selectedAppt.status))) {
        formData.append("isFollowUp", "true");
      }

      const response = await axios.put(`${backendUrl}/api/appointments/update`, formData);

      toast.success(response.data?.message || "Appointment updated successfully.");
      setShowUpdateModal(false);
      setSelectedAppt(null);
      fetchHistory();
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Could not update appointment.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubmit = async () => {
    if (!selectedAppt) return;

    if (cancelMessage.trim().length < 15) {
      toast.error("Cancel message must be at least 15 characters.");
      return;
    }

    try {
      setUpdating(true);

      await axios.post(`${backendUrl}/api/appointments/cancel`, {
        appointmentId: selectedAppt.id,
        bookingId: selectedAppt.booking_id,
        fullName: selectedAppt.fullName,
        phone: mobile,
        reason: cancelReason,
        message: cancelMessage,
      });

      toast.success("Appointment cancelled successfully.");
      setShowCancelModal(false);
      setSelectedAppt(null);
      fetchHistory();
    } catch (error) {
      console.error(error);
      toast.error("Could not cancel appointment.");
    } finally {
      setUpdating(false);
    }
  };

  const totalAppointments = appointments.length;

  const scheduledCount = appointments.filter((a) =>
    activeStatuses.includes(normalizeStatus(a.status))
  ).length;

  const missedCount = appointments.filter((a) =>
    ["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(normalizeStatus(a.status))
  ).length;

  const cancelledCount = appointments.filter(
    (a) => normalizeStatus(a.status) === "CANCELLED"
  ).length;

  const completedCount = appointments.filter(
    (a) => normalizeStatus(a.status) === "COMPLETED"
  ).length;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-2xl bg-white text-gray-500 animate-pulse">
        Loading appointments...
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white py-20 text-center shadow-sm">
        <FaCalendarCheck className="mx-auto mb-4 text-gray-300" size={60} />
        <h2 className="text-xl font-bold text-gray-700">No Appointments Found</h2>
        <p className="mt-1 text-gray-500">
          You haven't booked any appointments with this number yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
            <FaCalendarCheck size={22} />
          </div>

          <div>
            <h2 className="text-2xl font-extrabold text-gray-900">
              Appointment History
            </h2>
            <p className="text-sm text-gray-500">
              View consultation status, join video call, download reports and book follow-up.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
          <p className="text-sm font-semibold text-blue-600">Total</p>
          <h3 className="mt-2 text-3xl font-black text-gray-900">{totalAppointments}</h3>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
          <p className="text-sm font-semibold text-emerald-600">Active / Upcoming</p>
          <h3 className="mt-2 text-3xl font-black text-gray-900">{scheduledCount}</h3>
        </div>

        <div className="rounded-2xl border border-green-100 bg-green-50/60 p-4">
          <p className="text-sm font-semibold text-green-600">Completed</p>
          <h3 className="mt-2 text-3xl font-black text-gray-900">{completedCount}</h3>
        </div>

        <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
          <p className="text-sm font-semibold text-rose-600">Missed / No Show</p>
          <h3 className="mt-2 text-3xl font-black text-gray-900">{missedCount}</h3>
        </div>

        <div className="rounded-2xl border border-red-100 bg-red-50/60 p-4">
          <p className="text-sm font-semibold text-red-600">Cancelled</p>
          <h3 className="mt-2 text-3xl font-black text-gray-900">{cancelledCount}</h3>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="hidden grid-cols-12 gap-4 border-b border-gray-100 bg-gray-50 px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 lg:grid">
          <div className="col-span-4">Appointment Details</div>
          <div className="col-span-2">Date & Time</div>
          <div className="col-span-2">Doctor / Department</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        <div className="divide-y divide-gray-100">
          {appointments.map((appt, index) => (
            <div
              key={appt.id || index}
              className="relative grid grid-cols-1 gap-4 px-5 py-5 transition-all duration-300 hover:bg-gray-50 lg:grid-cols-12 lg:items-start lg:px-6"
            >
              <div className="absolute left-2 top-8 hidden lg:block">
                <span className={`block h-3 w-3 rounded-full ring-4 ring-white ${getStatusDot(appt.status)}`} />
              </div>

              <div className="lg:col-span-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <FaStethoscope size={20} />
                  </div>

                  <div>
                    <h3 className="text-base font-extrabold text-gray-900">
                      {appt.department || "General Consultation"}
                    </h3>

                    <p className="mt-1 text-sm font-medium text-gray-500">
                      Patient:{" "}
                      <span className="font-bold text-gray-700">
                        {appt.fullName || "Patient"}
                      </span>
                    </p>

                    <p className="mt-2 inline-flex rounded-full bg-gray-100 px-3 py-1 font-mono text-xs font-bold text-green-600">
                      {appt.booking_id || "Waiting for Approval"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-blue-50 p-2 text-blue-500">
                    <FaCalendarCheck />
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase text-gray-400">Appointment</p>
                    <p className="font-bold text-gray-800">{appt.appointmentDate || "Date Pending"}</p>
                    <p className="text-sm font-medium text-gray-500">{appt.appointmentTime || "Time Pending"}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <p className="text-xs font-bold uppercase text-gray-400">Doctor</p>
                <p className="mt-1 font-bold text-gray-800">
                  {appt.doctor_name || "Not assigned yet"}
                </p>
                <p className="mt-2 text-xs font-bold uppercase text-gray-400">Department</p>
                <p className="mt-1 font-semibold text-gray-700">{appt.department || "N/A"}</p>
              </div>

              <div className="lg:col-span-2">
                {getStatusBadge(appt.status)}
                <p className="mt-2 text-xs font-medium text-gray-500">{getStatusText(appt.status)}</p>
                {appt.video_link && (
                  <p className="mt-2 text-xs font-semibold text-blue-600">{getJoinMessage(appt)}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 lg:col-span-2 lg:justify-end">
                <button
                  type="button"
                  onClick={() => openDetails(appt)}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-100"
                >
                  <FaEye />
                  Details
                </button>

                {appt.video_link && (
                  <button
                    type="button"
                    onClick={() => handleJoinConsultation(appt)}
                    disabled={!canJoinConsultation(appt)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition ${
                      canJoinConsultation(appt)
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "cursor-not-allowed bg-gray-200 text-gray-500"
                    }`}
                  >
                    Join Call
                  </button>
                )}

                {appt.report_url && (
                  <a
                    href={getFileUrl(appt.report_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
                  >
                    <FaDownload />
                    Report
                  </a>
                )}

                {canUpdateOrCancel(appt) && (
                  <>
                    <button
                      type="button"
                      onClick={() => openUpdate(appt, "UPDATE")}
                      className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-50"
                    >
                      <FaEdit />
                      Update
                    </button>

                    <button
                      type="button"
                      onClick={() => openCancel(appt)}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-xs font-bold text-red-700 transition hover:bg-red-50"
                    >
                      <FaBan />
                      Cancel
                    </button>
                  </>
                )}

                {canReAppointment(appt) && (
                  <button
                    type="button"
                    onClick={() => openUpdate(appt, "REAPPOINT")}
                    className="inline-flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-4 py-2 text-xs font-bold text-purple-700 transition hover:bg-purple-100"
                  >
                    <FaRedo />
                    Re-Appointment
                  </button>
                )}
              </div>

              <div className="lg:col-span-12">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">
                    Issue / Symptoms
                  </p>
                  <p className="text-sm font-medium leading-6 text-gray-700">
                    {appt.symptoms || "No symptoms added by patient."}
                  </p>
                </div>

                {(appt.video_link || appt.doctor_name || appt.consultation_notes || appt.prescription_text || appt.missed_reason) && (
                  <div className="mt-3 rounded-2xl border border-blue-100 bg-blue-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-500">
                      Online Consultation
                    </p>

                    <p className="mt-1 text-sm font-semibold text-blue-800">
                      Doctor: {appt.doctor_name || "Doctor will be assigned soon"}
                    </p>

                    <p className="mt-1 text-sm text-blue-700">{getJoinMessage(appt)}</p>

                    {appt.video_link && (
                      <p className="mt-1 text-xs font-semibold text-blue-600">
                        Active: {appt.link_active_from || "N/A"} to {appt.link_active_until || "N/A"}
                      </p>
                    )}

                    {appt.prescription_text && (
                      <p className="mt-2 text-sm font-semibold text-emerald-700">
                        Prescription available. Open details to view.
                      </p>
                    )}

                    {appt.missed_reason && (
                      <p className="mt-2 text-sm font-semibold text-red-700">
                        Reason: {appt.missed_reason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Details Modal - replace existing showDetailsModal block with this */}
{showDetailsModal && selectedAppt && (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-8">
    <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
      <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-gray-100 bg-white p-5">
        <div>
          <h3 className="text-xl font-extrabold text-gray-900">Appointment Details</h3>
          <p className="mt-1 text-sm text-gray-500">
            Doctor, video link, consultation notes and prescription details.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowDetailsModal(false);
            setSelectedAppt(null);
          }}
          className="rounded-full bg-gray-100 p-3 text-gray-600 hover:bg-gray-200"
        >
          <FaTimes />
        </button>
      </div>

      <div className="max-h-[78vh] overflow-y-auto p-5">
        <div className="space-y-4">
          <div className="rounded-xl bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase text-gray-400">Booking ID</p>
            <p className="font-bold text-gray-800">{selectedAppt.booking_id || "Waiting for Approval"}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InfoBox label="Patient" value={selectedAppt.fullName || "N/A"} />
            <InfoBox label="Department" value={selectedAppt.department || "N/A"} />
            <InfoBox label="Date" value={selectedAppt.appointmentDate || "N/A"} />
            <InfoBox label="Time" value={selectedAppt.appointmentTime || "N/A"} />
            <InfoBox label="Doctor" value={selectedAppt.doctor_name || "Doctor not assigned yet"} />
            <InfoBox label="Doctor Email" value={selectedAppt.doctor_email || "N/A"} />
          </div>

          <div className="rounded-xl bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase text-gray-400">Status</p>
            <div className="mt-2">{getStatusBadge(selectedAppt.status)}</div>
            <p className="mt-2 text-sm text-gray-600">{getStatusText(selectedAppt.status)}</p>
          </div>

          <div className="rounded-xl bg-gray-50 p-4">
            <p className="text-xs font-bold uppercase text-gray-400">Symptoms</p>
            <p className="mt-1 whitespace-pre-line text-sm font-semibold text-gray-700">
              {selectedAppt.symptoms || "No symptoms added."}
            </p>
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-xs font-bold uppercase text-blue-500">Video Consultation</p>
            <p className="mt-1 text-sm font-semibold text-blue-800">
              {getJoinMessage(selectedAppt)}
            </p>
            {selectedAppt.video_link && (
              <>
                <p className="mt-1 text-xs font-semibold text-blue-600">
                  Active: {selectedAppt.link_active_from || "N/A"} to {selectedAppt.link_active_until || "N/A"}
                </p>
                <button
                  type="button"
                  onClick={() => handleJoinConsultation(selectedAppt)}
                  disabled={!canJoinConsultation(selectedAppt)}
                  className={`mt-3 rounded-xl px-5 py-2 text-sm font-bold ${
                    canJoinConsultation(selectedAppt)
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "cursor-not-allowed bg-gray-200 text-gray-500"
                  }`}
                >
                  Join Video Call
                </button>
              </>
            )}
          </div>

          {selectedAppt.report_url && (
            <a
              href={getFileUrl(selectedAppt.report_url)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-3 text-sm font-bold text-white"
            >
              <FaDownload />
              Open Report
            </a>
          )}

          {selectedAppt.consultation_notes && (
            <div className="rounded-xl bg-purple-50 p-4">
              <p className="text-xs font-bold uppercase text-purple-500">Doctor Notes</p>
              <p className="mt-1 whitespace-pre-line text-sm font-medium text-gray-700">
                {selectedAppt.consultation_notes}
              </p>
            </div>
          )}

          {selectedAppt.prescription_text && (
            <div className="rounded-xl bg-green-50 p-4">
              <p className="text-xs font-bold uppercase text-green-500">Prescription</p>
              <p className="mt-1 whitespace-pre-line text-sm font-medium text-gray-700">
                {selectedAppt.prescription_text}
              </p>
            </div>
          )}

          {selectedAppt.missed_reason && (
            <div className="rounded-xl bg-red-50 p-4">
              <p className="text-xs font-bold uppercase text-red-500">Missed / No Show Reason</p>
              <p className="mt-1 text-sm font-medium text-gray-700">{selectedAppt.missed_reason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}


      {showUpdateModal && selectedAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-xl font-extrabold text-gray-900">
                {["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(normalizeStatus(selectedAppt.status))
                  ? "Book Re-Appointment"
                  : "Update Appointment"}
              </h3>

              <button
                onClick={() => setShowUpdateModal(false)}
                className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-700">Appointment Date</label>
                  <input
                    type="date"
                    value={editDate}
                    min={todayStr}
                    max={
                      ["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(normalizeStatus(selectedAppt.status))
                        ? getMaxReAppointmentDate(selectedAppt)
                        : undefined
                    }
                    onChange={(e) => {
                      setEditDate(e.target.value);
                      setEditTime("");
                      fetchSlotsForDate(e.target.value);
                    }}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-700">Department</label>
                  <input
                    type="text"
                    value={editDepartment}
                    onChange={(e) => setEditDepartment(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {["MISSED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED"].includes(normalizeStatus(selectedAppt.status)) && (
                <p className="rounded-xl bg-purple-50 p-3 text-sm font-semibold text-purple-700">
                  Missed/no-show appointment ka re-appointment original appointment date se 1 month ke andar allowed hai.
                </p>
              )}

              {editDate && (
                <div>
                  <label className="mb-2 block text-sm font-bold text-gray-700">Available Time Slots</label>

                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                    {timeSlots.map((slot) => {
                      const isCurrentSlot =
                        slot.time === (selectedAppt.rawTime || selectedAppt.appointmentTime) &&
                        editDate === (selectedAppt.rawDate || toYMD(selectedAppt.appointmentDate));

                      const disabled = (slot.isBooked || slot.isPast) && !isCurrentSlot;

                      return (
                        <button
                          key={slot.time}
                          type="button"
                          disabled={disabled}
                          onClick={() => setEditTime(slot.time)}
                          className={`rounded-lg border px-2 py-2 text-sm font-bold transition ${
                            disabled
                              ? "cursor-not-allowed border-red-100 bg-red-50 text-red-300 line-through"
                              : editTime === slot.time
                              ? "border-emerald-700 bg-emerald-600 text-white"
                              : "border-gray-300 bg-white text-gray-700 hover:border-emerald-500 hover:text-emerald-600"
                          }`}
                        >
                          {formatTime12Hour(slot.time)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700">Symptoms / Issue</label>
                <textarea
                  rows={3}
                  value={editSymptoms}
                  onChange={(e) => setEditSymptoms(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 outline-none focus:border-emerald-500"
                />
              </div>

              <button
                type="button"
                disabled={updating}
                onClick={handleUpdateSubmit}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700 disabled:bg-gray-400"
              >
                <FaSave />
                {updating ? "Saving..." : "Confirm Appointment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && selectedAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-xl font-extrabold text-gray-900">Cancel Appointment</h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700">Cancel Reason</label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 outline-none focus:border-red-500"
                >
                  <option value="Patient Request">Patient Request</option>
                  <option value="Wrong Date Selected">Wrong Date Selected</option>
                  <option value="Personal Reason">Personal Reason</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700">Message</label>
                <textarea
                  rows={4}
                  value={cancelMessage}
                  onChange={(e) => setCancelMessage(e.target.value)}
                  placeholder="Please write cancellation message..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 outline-none focus:border-red-500"
                />
                <p className="mt-1 text-xs text-gray-400">Minimum 15 characters required.</p>
              </div>

              <button
                type="button"
                disabled={updating}
                onClick={handleCancelSubmit}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 font-bold text-white hover:bg-red-700 disabled:bg-gray-400"
              >
                <FaBan />
                {updating ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoBox: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-4">
    <p className="text-xs font-bold uppercase text-gray-400">{label}</p>
    <p className="break-words font-bold text-gray-800">{value || "N/A"}</p>
  </div>
);


// ==========================================
// 2. MAIN PROFILE PAGE COMPONENT
// ==========================================
export const ProfilePage: React.FC<ProfilePageProps> = ({ onLogout, onProfileUpdate }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // 🔥 ADDED 'appointments' to activeSection state
  const [activeSection, setActiveSection] = useState<'profile' | 'addresses' | 'payments' | 'mystuff' | 'myorders' | 'appointments'>('profile'); 

  const [formData, setFormData] = useState<UserProfile>({
    id: '', firstName: '', lastName: '', email: '', mobile: '', profilePictureUrl: '/default-avatar.png',
  });

  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingMobile, setIsEditingMobile] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const getUserId = (): string | null => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { return JSON.parse(storedUser).id; } 
      catch (e) { return null; }
    }
    return null;
  };

  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setError("User not logged in or ID not found.");
      setLoading(false);
      return;
    }

    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("You are not logged in. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true); setError(null);
        const response = await axios.get<UserProfile>(
          `https://www.ayushvatika.com/api/auth/profile`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setUser(response.data);
        setFormData(response.data);
      } catch (error: any) {
        setError("Failed to fetch user profile.");
        toast.error("Failed to fetch user profile.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchUserProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profilePictureUrl: reader.result as string }));
        setIsEditingPersonalInfo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUploadClick = () => fileInputRef.current?.click();

  const handleSave = async (section: 'personal' | 'email' | 'mobile') => {
    if (!user) return;
    try {
      setLoading(true);
      const updatedData: Partial<UserProfile> = { id: user.id };

      if (section === "personal") {
        updatedData.firstName = formData.firstName ?? "";
        updatedData.lastName = formData.lastName ?? "";
        updatedData.profilePictureUrl = formData.profilePictureUrl ?? "";
      } else if (section === "email") updatedData.email = formData.email ?? "";
      else if (section === "mobile") updatedData.mobile = formData.mobile ?? "";

      const token = localStorage.getItem("token");
      const response = await axios.put<UserProfile>(
        "https://www.ayushvatika.com/api/auth/profile",
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(response.data);
      onProfileUpdate(response.data);

      localStorage.setItem("user", JSON.stringify({
          ...JSON.parse(localStorage.getItem("user") || "{}"),
          firstName: response.data.firstName, lastName: response.data.lastName,
          email: response.data.email, mobile: response.data.mobile,
          profilePictureUrl: response.data.profilePictureUrl, picture: response.data.profilePictureUrl 
      }));

      window.dispatchEvent(new Event('userDataUpdated'));

      if (section === "personal") setIsEditingPersonalInfo(false);
      else if (section === "email") setIsEditingEmail(false);
      else if (section === "mobile") setIsEditingMobile(false);

      toast.success("Profile updated successfully!");
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to update ${section} profile.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (section: 'personal' | 'email' | 'mobile') => {
    if (user) {
      if (section === 'personal') {
        setFormData(prev => ({ ...prev, firstName: user.firstName, lastName: user.lastName, profilePictureUrl: user.profilePictureUrl }));
        setIsEditingPersonalInfo(false);
      } else if (section === 'email') {
        setFormData(prev => ({ ...prev, email: user.email }));
        setIsEditingEmail(false);
      } else if (section === 'mobile') {
        setFormData(prev => ({ ...prev, mobile: user.mobile }));
        setIsEditingMobile(false);
      }
    }
  };

  const handleNavClick = (section: typeof activeSection) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false); 
  };

  const NavItem = ({ section, label, icon }: { section: typeof activeSection, label: string, icon: React.ReactNode }) => (
    <li 
      className={`flex items-center p-3 rounded-lg cursor-pointer whitespace-nowrap transition-all ${
        activeSection === section 
          ? 'bg-emerald-50 border-l-4 border-emerald-600 text-emerald-700 font-semibold shadow-sm' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
      }`}
      onClick={() => handleNavClick(section)}
    >
      <div className={`mr-3 ${activeSection === section ? 'text-emerald-700' : 'text-gray-400'}`}>{icon}</div>
      {label}
    </li>
  );

  const getPageTitle = () => {
    switch(activeSection) {
      case 'profile': return 'Profile Info';
      case 'myorders': return 'My Orders';
      case 'addresses': return 'Addresses';
      case 'appointments': return 'Appointment History'; // 🔥 Added case
      case 'payments': return 'Payments';
      case 'mystuff': return 'My Stuff';
      default: return 'Profile';
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen bg-gray-100 text-emerald-700 animate-pulse">Loading profile...</div>;
  if (error) return <div className="flex flex-col justify-center items-center h-screen bg-gray-100 text-red-600"><p>{error}</p><button onClick={onLogout} className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg">Go to Login</button></div>;
  if (!user) return <div className="flex justify-center items-center h-screen bg-gray-100 text-gray-700">No user data available.</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row md:p-4 font-inter gap-4 relative">
      
      {/* --- MOBILE HEADER STRIP --- */}
      <div className="md:hidden flex justify-between items-center bg-white h-14 px-4 border-b sticky top-0 z-20">
        <h2 className="text-lg font-bold text-gray-800">{getPageTitle()}</h2>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition">
          {isMobileMenuOpen ? (
             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          ) : (
             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
          )}
        </button>
      </div>

      {/* --- MOBILE OVERLAY --- */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-[120px] z-30 md:hidden bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}

      {/* --- SIDEBAR MENU --- */}
      <div className={`
          fixed md:sticky             
          z-40 md:z-0
          top-[120px] md:top-32       
          md:self-start               
          right-0 md:left-0        
          h-[calc(100vh-60px)] md:h-fit 
          w-[75%] md:w-1/4         
          bg-white md:rounded-xl shadow-2xl md:shadow-lg 
          p-4 md:p-6 flex flex-col transition-transform duration-300 ease-in-out
          border-l md:border-none
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
      `}>
        
        <div className="flex flex-col items-center gap-2 mb-6 border-b pb-4 pt-2">
          <div className="text-center">
             <p className="text-3xl font-bold text-gray-800 ">{formData.firstName} {formData.lastName}</p>
             <p className="text-xs text-gray-500 truncate max-w-[180px]">{formData.email}</p>
          </div>
        </div>

        <nav className="w-full overflow-y-auto custom-scrollbar flex-1">
          <ul className="flex flex-col space-y-1">
            <NavItem section="profile" label="Profile Info" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>} />
            <NavItem section="myorders" label="My Orders" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>} />
            <NavItem section="addresses" label="Addresses" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657M17.657 16.657A8 8 0 1118 8a8 8 0 01-1.071 3.514L12 21l-4.929-7.371A8 8 0 0118 8z"></path></svg>} />
            <NavItem section="payments" label="Payments" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>} />
            <NavItem section="mystuff" label="My Stuff" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>} />
            
            {/* 🔥 NEW NAV ITEM ADDED HERE */}
            <NavItem section="appointments" label="Appointment History" icon={<FaCalendarCheck className="w-5 h-5" />} />

            <li className="flex items-center p-3 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition mt-auto pt-4 border-t whitespace-nowrap" onClick={onLogout}>
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
              Logout
            </li>
          </ul>
        </nav>
      </div>

      {/* 2. Main Content Area */}
      <div className="w-full md:w-3/4 flex-1 p-4 md:p-0">
        
        {/* === Profile Section === */}
        {activeSection === 'profile' && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 space-y-6 md:space-y-8 animate-fade-in">
            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Personal Information</h3>
                {!isEditingPersonalInfo ? (
                  <button onClick={() => setIsEditingPersonalInfo(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('personal')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('personal')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 mb-6">
                  <div className="relative group">
                    <img 
                        src={formData.profilePictureUrl || '/default-avatar.png'} 
                        className="w-20 h-20 rounded-full object-cover border border-gray-200"
                        onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                    />
                    <button 
                        onClick={handleImageUploadClick}
                        className="absolute bottom-0 -right-2 bg-white text-gray-700 p-1.5 rounded-full shadow border border-gray-200 hover:bg-gray-50 transition"
                        title="Change Photo"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Profile Picture</p>
                    <p className="text-xs text-gray-400">Click icon to update</p>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-500 text-xs uppercase font-bold mb-1">First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} readOnly={!isEditingPersonalInfo} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingPersonalInfo ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs uppercase font-bold mb-1">Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} readOnly={!isEditingPersonalInfo} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingPersonalInfo ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Email Address</h3>
                {!isEditingEmail ? (
                  <button onClick={() => setIsEditingEmail(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('email')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('email')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>
              <input type="email" name="email" value={formData.email} onChange={handleChange} readOnly={!isEditingEmail} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingEmail ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
            </div>

            <div className="border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">Mobile Number</h3>
                {!isEditingMobile ? (
                  <button onClick={() => setIsEditingMobile(true)} className="text-blue-600 hover:underline font-medium text-sm">Edit</button>
                ) : (
                  <div className="space-x-2">
                    <button onClick={() => handleSave('mobile')} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm" disabled={loading}>Save</button>
                    <button onClick={() => handleCancel('mobile')} className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm" disabled={loading}>Cancel</button>
                  </div>
                )}
              </div>
              <input type="text" name="mobile" value={formData.mobile ?? ""} onChange={handleChange} readOnly={!isEditingMobile} className={`w-full border rounded-lg px-3 py-2 text-sm ${isEditingMobile ? 'bg-white focus:ring-emerald-500' : 'bg-gray-50 text-gray-600'}`} />
            </div>
          </div>
        )}

        {/* === Dynamic Sections === */}
        {activeSection === 'myorders' && user && <MyOrders userId={user.id} />}
        {activeSection === 'addresses' && user && <ManageAddressesPage userId={user.id} />}
        
        {/* 🔥 NEW APPOINTMENT SECTION RENDER */}
        {activeSection === 'appointments' && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 animate-fade-in min-h-[500px]">
            {user?.mobile ? (
               <AppointmentHistory mobile={user.mobile} />
            ) : (
               <div className="text-center py-20">
                  <h2 className="text-xl font-bold text-gray-700">Mobile Number Required</h2>
                  <p className="text-gray-500 mt-2">Please update your mobile number in the "Profile Info" tab to view your appointments.</p>
               </div>
            )}
          </div>
        )}

        {activeSection === 'payments' && (
          <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col justify-center items-center text-center">
             <div className="bg-purple-50 p-4 rounded-full mb-4">
                <svg className="w-10 h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Saved Payments</h3>
            <p className="text-gray-500">Manage your saved cards and UPI IDs.<br/><span className="text-sm font-semibold text-emerald-600">Coming Soon!</span></p>
          </div>
        )}
        
        {activeSection === 'mystuff' && (
          <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col justify-center items-center text-center">
             <div className="bg-yellow-50 p-4 rounded-full mb-4">
                <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">My Stuff</h3>
            <p className="text-gray-500">Your wishlist, reviews, and more.<br/><span className="text-sm font-semibold text-emerald-600">Coming Soon!</span></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;